/**
 * ProgressIndicator 進度指示器元件
 *
 * 提供多種進度顯示方式：
 * - 旋轉載入器（Spinner）
 * - 進度條（ProgressBar）
 * - 步驟指示器（Stepper）
 */

// ========================================================================
// 型別定義
// ========================================================================

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerOptions {
  size?: SpinnerSize;
  color?: string;
  message?: string;
}

export interface ProgressBarOptions {
  value?: number;
  max?: number;
  showLabel?: boolean;
  color?: string;
  height?: string;
}

export interface Step {
  label: string;
  description?: string;
}

export interface StepperOptions {
  steps: Step[];
  currentStep?: number;
  orientation?: 'horizontal' | 'vertical';
}

// ========================================================================
// 樣式常數
// ========================================================================

const SPINNER_SIZES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

// ========================================================================
// Spinner 類別
// ========================================================================

export class Spinner {
  private element: HTMLDivElement;
  private options: Required<Omit<SpinnerOptions, 'message'>> & Pick<SpinnerOptions, 'message'>;

  constructor(options: SpinnerOptions = {}) {
    this.options = {
      size: options.size ?? 'md',
      color: options.color ?? 'primary-600',
      message: options.message,
    };

    this.element = this.createElement();
  }

  getElement(): HTMLDivElement {
    return this.element;
  }

  setMessage(message: string): void {
    const messageEl = this.element.querySelector('p');
    if (messageEl) {
      messageEl.textContent = message;
    } else if (message) {
      const p = document.createElement('p');
      p.className = 'mt-2 text-sm text-gray-600';
      p.textContent = message;
      this.element.appendChild(p);
    }
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private createElement(): HTMLDivElement {
    const { size, color, message } = this.options;

    const element = document.createElement('div');
    element.className = 'flex flex-col items-center justify-center';

    element.innerHTML = `
      <div class="inline-block ${SPINNER_SIZES[size]} animate-spin rounded-full border-4 border-solid border-${color} border-r-transparent" role="status">
        <span class="sr-only">載入中...</span>
      </div>
      ${message ? `<p class="mt-2 text-sm text-gray-600">${message}</p>` : ''}
    `;

    return element;
  }
}

// ========================================================================
// ProgressBar 類別
// ========================================================================

export class ProgressBar {
  private element: HTMLDivElement;
  private bar: HTMLDivElement;
  private label: HTMLSpanElement | null = null;
  private options: Required<ProgressBarOptions>;
  private currentValue: number;

  constructor(options: ProgressBarOptions = {}) {
    this.options = {
      value: options.value ?? 0,
      max: options.max ?? 100,
      showLabel: options.showLabel ?? true,
      color: options.color ?? 'primary-600',
      height: options.height ?? '8px',
    };

    this.currentValue = this.options.value;
    this.element = this.createElement();
    this.bar = this.element.querySelector('.progress-fill') as HTMLDivElement;

    if (this.options.showLabel) {
      this.label = this.element.querySelector('.progress-label');
    }

    this.updateProgress();
  }

  getElement(): HTMLDivElement {
    return this.element;
  }

  getValue(): number {
    return this.currentValue;
  }

  setValue(value: number): void {
    this.currentValue = Math.min(Math.max(0, value), this.options.max);
    this.updateProgress();
  }

  increment(amount: number = 1): void {
    this.setValue(this.currentValue + amount);
  }

  reset(): void {
    this.setValue(0);
  }

  complete(): void {
    this.setValue(this.options.max);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private createElement(): HTMLDivElement {
    const { showLabel, color, height } = this.options;

    const element = document.createElement('div');
    element.className = 'w-full';

    element.innerHTML = `
      ${showLabel ? `
        <div class="flex justify-between mb-1">
          <span class="text-sm font-medium text-gray-700">進度</span>
          <span class="text-sm font-medium text-gray-700 progress-label">0%</span>
        </div>
      ` : ''}
      <div class="w-full bg-gray-200 rounded-full overflow-hidden" style="height: ${height}">
        <div class="progress-fill bg-${color} rounded-full transition-all duration-300 ease-out" style="height: 100%; width: 0%"></div>
      </div>
    `;

    return element;
  }

  private updateProgress(): void {
    const percentage = Math.round((this.currentValue / this.options.max) * 100);

    this.bar.style.width = `${percentage}%`;

    if (this.label) {
      this.label.textContent = `${percentage}%`;
    }
  }
}

// ========================================================================
// Stepper 類別
// ========================================================================

export class Stepper {
  private element: HTMLDivElement;
  private options: Required<StepperOptions>;
  private currentStep: number;

  constructor(options: StepperOptions) {
    this.options = {
      steps: options.steps,
      currentStep: options.currentStep ?? 0,
      orientation: options.orientation ?? 'horizontal',
    };

    this.currentStep = this.options.currentStep;
    this.element = this.createElement();
  }

  getElement(): HTMLDivElement {
    return this.element;
  }

  getCurrentStep(): number {
    return this.currentStep;
  }

  setCurrentStep(step: number): void {
    this.currentStep = Math.min(Math.max(0, step), this.options.steps.length - 1);
    this.updateSteps();
  }

  nextStep(): void {
    this.setCurrentStep(this.currentStep + 1);
  }

  prevStep(): void {
    this.setCurrentStep(this.currentStep - 1);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.element.remove();
  }

  private createElement(): HTMLDivElement {
    const { steps, orientation } = this.options;

    const element = document.createElement('div');
    element.className = orientation === 'horizontal'
      ? 'flex items-center justify-between'
      : 'flex flex-col space-y-4';

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step) continue;

      const stepEl = this.createStepElement(i, step);
      element.appendChild(stepEl);

      // 加入連接線（非最後一個步驟）
      if (i < steps.length - 1 && orientation === 'horizontal') {
        const connector = document.createElement('div');
        connector.className = 'flex-1 h-0.5 bg-gray-200 mx-4 step-connector';
        connector.dataset.index = String(i);
        element.appendChild(connector);
      }
    }

    return element;
  }

  private createStepElement(index: number, step: Step): HTMLDivElement {
    const stepEl = document.createElement('div');
    stepEl.className = 'flex items-center step-item';
    stepEl.dataset.index = String(index);

    stepEl.innerHTML = `
      <div class="flex items-center justify-center w-10 h-10 rounded-full border-2 step-circle transition-colors">
        <span class="text-sm font-semibold step-number">${index + 1}</span>
      </div>
      <div class="ml-3">
        <p class="text-sm font-medium step-label">${step.label}</p>
        ${step.description ? `<p class="text-xs text-gray-500">${step.description}</p>` : ''}
      </div>
    `;

    this.updateStepElement(stepEl, index);

    return stepEl;
  }

  private updateSteps(): void {
    const stepItems = this.element.querySelectorAll('.step-item');
    const connectors = this.element.querySelectorAll('.step-connector');

    stepItems.forEach((item) => {
      const index = parseInt(item.getAttribute('data-index') ?? '0', 10);
      this.updateStepElement(item as HTMLDivElement, index);
    });

    connectors.forEach((connector) => {
      const index = parseInt(connector.getAttribute('data-index') ?? '0', 10);
      if (index < this.currentStep) {
        connector.classList.remove('bg-gray-200');
        connector.classList.add('bg-primary-600');
      } else {
        connector.classList.remove('bg-primary-600');
        connector.classList.add('bg-gray-200');
      }
    });
  }

  private updateStepElement(stepEl: HTMLDivElement, index: number): void {
    const circle = stepEl.querySelector('.step-circle');
    const number = stepEl.querySelector('.step-number');
    const label = stepEl.querySelector('.step-label');

    if (!circle || !number || !label) return;

    // 移除所有狀態類別
    circle.classList.remove(
      'border-gray-300', 'bg-white', 'text-gray-500',
      'border-primary-600', 'bg-primary-600', 'text-white',
      'border-green-600', 'bg-green-600'
    );
    label.classList.remove('text-gray-500', 'text-primary-600', 'text-gray-900');

    if (index < this.currentStep) {
      // 已完成
      circle.classList.add('border-green-600', 'bg-green-600', 'text-white');
      number.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
      `;
      label.classList.add('text-gray-900');
    } else if (index === this.currentStep) {
      // 當前步驟
      circle.classList.add('border-primary-600', 'bg-primary-600', 'text-white');
      number.textContent = String(index + 1);
      label.classList.add('text-primary-600');
    } else {
      // 未完成
      circle.classList.add('border-gray-300', 'bg-white', 'text-gray-500');
      number.textContent = String(index + 1);
      label.classList.add('text-gray-500');
    }
  }
}

// ========================================================================
// 工廠函式
// ========================================================================

/**
 * 建立全頁載入遮罩
 */
export function createLoadingOverlay(message?: string): {
  show: () => void;
  hide: () => void;
  setMessage: (msg: string) => void;
} {
  const overlay = document.createElement('div');
  overlay.className = [
    'fixed',
    'inset-0',
    'z-50',
    'flex',
    'items-center',
    'justify-center',
    'bg-white',
    'bg-opacity-75',
    'hidden',
  ].join(' ');

  const spinner = new Spinner({ size: 'lg', message });
  spinner.mount(overlay);

  document.body.appendChild(overlay);

  return {
    show: () => overlay.classList.remove('hidden'),
    hide: () => overlay.classList.add('hidden'),
    setMessage: (msg: string) => spinner.setMessage(msg),
  };
}
